import i18next, { type i18n, type Resource } from 'i18next';
import ICU from 'i18next-icu';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enCommon from './namespaces/en/common.json';
import enOperator from './namespaces/en/operator.json';
import enCommissioning from './namespaces/en/commissioning.json';
import enVideo from './namespaces/en/video.json';
import arCommon from './namespaces/ar/common.json';
import arOperator from './namespaces/ar/operator.json';
import arCommissioning from './namespaces/ar/commissioning.json';
import arVideo from './namespaces/ar/video.json';

import { DEFAULT_LOCALE, type Namespace, type SupportedLocale } from './types';
import { syncDocumentDirection } from './utils/direction';

export interface BootstrapI18nOptions {
  initialLocale?: SupportedLocale;
  /** Namespaces this SPA actually uses; loaded eagerly. */
  namespaces?: ReadonlyArray<Namespace>;
  /** Disable automatic browser language detection (e.g. tests). */
  detectLanguage?: boolean;
}

const RESOURCES: Record<SupportedLocale, Partial<Record<Namespace, unknown>>> = {
  en: { common: enCommon, operator: enOperator, commissioning: enCommissioning, video: enVideo },
  ar: { common: arCommon, operator: arOperator, commissioning: arCommissioning, video: arVideo },
};

/**
 * Initialise i18next with ICU MessageFormat, react-i18next bindings, and the
 * shared CGuard catalogs. SPAs call this once during bootstrap.
 *
 * The function is idempotent — repeated calls return the existing instance.
 */
export async function bootstrapI18n(options: BootstrapI18nOptions = {}): Promise<i18n> {
  const initialLocale = options.initialLocale ?? DEFAULT_LOCALE;
  const namespaces = options.namespaces ?? ['common', 'operator'];
  const detectLanguage = options.detectLanguage ?? true;

  if (i18next.isInitialized) {
    return i18next;
  }

  let chain = i18next.use(ICU).use(initReactI18next);
  if (detectLanguage) {
    chain = chain.use(LanguageDetector);
  }

  await chain.init({
    resources: buildResources(namespaces),
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    ns: [...namespaces],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  });

  syncDocumentDirection((i18next.language as SupportedLocale) ?? initialLocale);
  return i18next;
}

function buildResources(namespaces: ReadonlyArray<Namespace>): Resource {
  const out: Resource = {};
  for (const lang of Object.keys(RESOURCES) as SupportedLocale[]) {
    const bundle = RESOURCES[lang];
    const langOut: Record<string, unknown> = {};
    for (const ns of namespaces) {
      const data = bundle[ns];
      if (data) langOut[ns] = data;
    }
    out[lang] = langOut as Resource[string];
  }
  return out;
}

/** Switch active locale + sync document direction. */
export async function setLocale(next: SupportedLocale): Promise<void> {
  await i18next.changeLanguage(next);
  syncDocumentDirection(next);
}

export {
  DEFAULT_LOCALE,
  namespaces,
  supportedLocales,
  type Namespace,
  type SupportedLocale,
} from './types';
export { directionFor, isRtl, syncDocumentDirection, type Direction } from './utils/direction';
export {
  formatDate,
  formatDateTime,
  formatDateTimeRange,
  formatRelative,
  formatTime,
} from './utils/format-date';
export {
  useComponentLabels,
  usePagerLabels,
  useProvenanceLabels,
  useShellLabels,
  useTableLabels,
  type CommonComponentLabels,
  type CommonPagerLabels,
  type CommonShellLabels,
  type CommonTableLabels,
} from './utils/labels';
